import React, { useState, useEffect } from 'react';
import { ZeitaufzeichnungCreate, Zeitaufzeichnung, Verschmutzungsgrad } from '../services/zeitaufzeichnungApi';
import '../styles/ZeitaufzeichnungForm.css';

interface ZeitaufzeichnungFormProps {
  onSubmit: (data: ZeitaufzeichnungCreate) => Promise<void>;
  onCancel: () => void;
  initialData?: Zeitaufzeichnung;
  selectedDate?: string; // YYYY-MM-DD
}

const ZeitaufzeichnungForm: React.FC<ZeitaufzeichnungFormProps> = ({
  onSubmit,
  onCancel,
  initialData,
  selectedDate
}) => {
  const [formData, setFormData] = useState<ZeitaufzeichnungCreate>({
    datum: selectedDate || initialData?.datum || new Date().toISOString().split('T')[0],
    beginn: initialData?.beginn || '08:00',
    ende: initialData?.ende || '17:00',
    mittagspause_minuten: initialData?.mittagspause_minuten || 30,
    verschmutzungsgrad: initialData?.verschmutzungsgrad || null,
    notiz: initialData?.notiz || ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      setFormData(prev => ({ ...prev, datum: selectedDate }));
    }
  }, [selectedDate]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.datum) {
      newErrors.datum = 'Datum ist erforderlich';
    }

    if (!formData.beginn) {
      newErrors.beginn = 'Beginn ist erforderlich';
    }

    if (!formData.ende) {
      newErrors.ende = 'Ende ist erforderlich';
    }

    if (formData.beginn && formData.ende && formData.beginn >= formData.ende) {
      newErrors.ende = 'Ende muss nach Beginn liegen';
    }

    if (formData.mittagspause_minuten < 0) {
      newErrors.mittagspause_minuten = 'Mittagspause muss positiv sein';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Fehler beim Speichern';
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'mittagspause_minuten'
        ? parseInt(value) || 0
        : name === 'verschmutzungsgrad'
          ? (value === '' ? null : value as Verschmutzungsgrad)
          : value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <div className="zeitaufzeichnung-form-overlay">
      <div className="zeitaufzeichnung-form-container">
        <h2>{initialData ? 'Zeitaufzeichnung bearbeiten' : 'Neue Zeitaufzeichnung'}</h2>

        <form onSubmit={handleSubmit} className="zeitaufzeichnung-form">
          <div className="form-group">
            <label htmlFor="datum">Datum *</label>
            <input
              type="date"
              id="datum"
              name="datum"
              value={formData.datum}
              onChange={handleChange}
              className={errors.datum ? 'error' : ''}
              required
            />
            {errors.datum && <span className="error-message">{errors.datum}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="beginn">Beginn *</label>
              <input
                type="time"
                id="beginn"
                name="beginn"
                value={formData.beginn}
                onChange={handleChange}
                className={errors.beginn ? 'error' : ''}
                required
              />
              {errors.beginn && <span className="error-message">{errors.beginn}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="ende">Ende *</label>
              <input
                type="time"
                id="ende"
                name="ende"
                value={formData.ende}
                onChange={handleChange}
                className={errors.ende ? 'error' : ''}
                required
              />
              {errors.ende && <span className="error-message">{errors.ende}</span>}
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="mittagspause_minuten">Mittagspause (Minuten) *</label>
            <input
              type="number"
              id="mittagspause_minuten"
              name="mittagspause_minuten"
              value={formData.mittagspause_minuten}
              onChange={handleChange}
              className={errors.mittagspause_minuten ? 'error' : ''}
              min="0"
              step="5"
              required
            />
            {errors.mittagspause_minuten && (
              <span className="error-message">{errors.mittagspause_minuten}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="verschmutzungsgrad">Verschmutzungsgrad</label>
            <select
              id="verschmutzungsgrad"
              name="verschmutzungsgrad"
              value={formData.verschmutzungsgrad || ''}
              onChange={handleChange}
              className={errors.verschmutzungsgrad ? 'error' : ''}
            >
              <option value="">-- Bitte wählen (optional) --</option>
              <option value="LEICHT">Leicht</option>
              <option value="MITTEL">Mittel</option>
              <option value="STARK">Stark - Starke Verschmutzung</option>
            </select>
            <small className="form-help">Nur für Feldarbeiter (Meister, Geselle, Lehrling)</small>
            {errors.verschmutzungsgrad && (
              <span className="error-message">{errors.verschmutzungsgrad}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="notiz">Notiz</label>
            <textarea
              id="notiz"
              name="notiz"
              value={formData.notiz}
              onChange={handleChange}
              rows={3}
              placeholder="Optional: Notizen zur Zeitaufzeichnung"
            />
          </div>

          {errors.submit && (
            <div className="error-message submit-error">{errors.submit}</div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={onCancel}
              className="btn-secondary"
              disabled={isSubmitting}
            >
              Abbrechen
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Speichern...' : 'Speichern'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ZeitaufzeichnungForm;
